/**
 * File: App.js
 * Author: Herve Emissah
 * Created: 2021-08-15
 * Description: Main React component for the NMO SWC QC web interface.
 *              Handles files folder selection, log viewing, and downloading of standardized files.
 */

import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import logo from './nmo_swc_qc_logo.png';
import LogViewer from './LogViewer';

function App() {
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [error, setError] = useState(null);
  const [logContent, setLogContent] = useState('');
  const [checkCorrectBranchTag, setCheckCorrectBranchTag] = useState(false);
  const [checkLongConnections, setCheckLongConnections] = useState(true);
  const [branchtype, setbranchtype] = useState(2);
  const [stdevX, setStdevX] = useState(6);
  const [isSaving, setIsSaving] = useState(false);
  const [systemBusy, setSystemBusy] = useState(false);

  const branchSelectRef = useRef(null);
  const branchTextRef = useRef(null);
  const stdevSelectRef = useRef(null);
  const stdevTextRef = useRef(null);

  const branchLabels = {
    2: 'Axon (2)',
    3: 'Basal dendrites (3)',
    4: 'Apical dendrites (4)',
    5: 'Other dendrites (5)',
    6: 'Unspecified neurites (6)',
    7: 'Glial processes (7)',
  };

  useEffect(() => {
    if (branchSelectRef.current && branchTextRef.current) {
      branchSelectRef.current.style.width =
        branchTextRef.current.offsetWidth + 32 + 'px';
    }
  }, [branchtype]);

  useEffect(() => {
    if (stdevSelectRef.current && stdevTextRef.current) {
      stdevSelectRef.current.style.width =
        stdevTextRef.current.offsetWidth + 32 + 'px';
    }
  }, [stdevX]);

  const handleFileChange = (e) => {
    const files = e.target.files;
    setSelectedFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFiles || selectedFiles.length === 0) {
      window.alert('Please select SWC files first.');
      return;
    }

    setIsSaving(true);

    const formData = new FormData();
    for (const file of selectedFiles) {
      formData.append('files', file);
    }

    try {
      const response = await fetch('/nmo/upload', {
        method: 'POST',
        body: formData,
      });

      let responseText = '';
      try {
        responseText = await response.text();
      } catch (readError) {
        console.error('[UPLOAD] Could not read response body:', readError);
      }

      console.log('[UPLOAD] status:', response.status, response.statusText);
      console.log('[UPLOAD] response body:', responseText);

      if (response.status === 429) {
        setSystemBusy(true);
        window.alert('System currently in use. Please try again later.');
        return;
      }

      if (response.ok) {
        console.log('[UPLOAD] Upload completed successfully.');
      } else {
        window.alert(
          `Failed to upload files to server.\n\n` +
          `HTTP Status: ${response.status} ${response.statusText}\n\n` +
          `Server said:\n${responseText || 'No response body returned.'}`
        );
      }
    } catch (error) {
      console.error('[UPLOAD] Error:', error);
      window.alert(
        `Error uploading files.\n\n` +
        `${error.name}: ${error.message}`
      );
    } finally {
      setIsSaving(false);
      setSystemBusy(false);
    }
  };

  const handleStandardizeClick = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      window.alert('Please select SWC files first.');
      return;
    }

    setIsSaving(true);
    setSystemBusy(false);

    const startTime = Date.now();

    try {
      const formData = new FormData();

      for (const file of selectedFiles) {
        formData.append('files', file);
      }

      formData.append('checkCorrectBranchTag', checkCorrectBranchTag);
      formData.append('branchtype', branchtype);

      console.log(
        `[STD] Starting Standardize for ${selectedFiles.length} files`
      );
      console.log('[STD] Correct Branch Tag:', checkCorrectBranchTag);
      console.log('[STD] Branch Type:', branchtype);

      // No browser-side timeout is intentionally configured.
      const response = await fetch('/nmo/SWC_STD', {
        method: 'POST',
        body: formData,
      });

      let responseText = '';

      try {
        responseText = await response.text();
      } catch (readError) {
        console.error('[STD] Could not read response body:', readError);
      }

      const elapsedSeconds =
        ((Date.now() - startTime) / 1000).toFixed(1);

      console.log(
        '[STD] HTTP status:',
        response.status,
        response.statusText
      );
      console.log('[STD] Response body:', responseText);
      console.log(
        `[STD] Request elapsed time: ${elapsedSeconds} seconds`
      );

      if (response.status === 429) {
        setSystemBusy(true);
        window.alert('System currently in use. Please try again later.');
        return;
      }

      if (response.ok) {
        console.log('[STD] Standardize completed successfully.');

        const shouldDownload = window.confirm(
          `Standardize process completed successfully.\n` +
          `Processing time: ${elapsedSeconds} seconds.\n\n` +
          `Do you want to download the files?`
        );

        if (shouldDownload) {
          await handleDownloadClick();
        }

        setSystemBusy(false);
        return;
      }

      console.error(
        '[STD] FAILED - HTTP status:',
        response.status,
        response.statusText
      );
      console.error(
        '[STD] FAILED - response body:',
        responseText
      );

      window.alert(
        `Failed to complete Standardize process.\n\n` +
        `HTTP Status: ${response.status} ${response.statusText}\n\n` +
        `Elapsed Time: ${elapsedSeconds} seconds\n\n` +
        `Server said:\n${responseText || 'No response body returned.'}\n\n` +
        `IMPORTANT:\n` +
        `The server-side processing may still be running. ` +
        `Check the processing log before starting another job.`
      );
    } catch (error) {
      const elapsedSeconds =
        ((Date.now() - startTime) / 1000).toFixed(1);

      console.error('[STD] FETCH ERROR:', error);
      console.error('[STD] Error name:', error.name);
      console.error('[STD] Error message:', error.message);
      console.error(
        `[STD] Failed after ${elapsedSeconds} seconds`
      );

      window.alert(
        `Connection error during Standardize process.\n\n` +
        `Error Type: ${error.name}\n` +
        `Error Message: ${error.message}\n\n` +
        `Elapsed Time: ${elapsedSeconds} seconds\n\n` +
        `IMPORTANT:\n` +
        `The server-side process may still be running. ` +
        `Check the processing log before attempting another Standardize request.`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAutoConnectClick = async () => {
     if (!selectedFiles || selectedFiles.length === 0) {
       window.alert("Please select SWC files first.");
       return;
     }

     setIsSaving(true);
     setSystemBusy(false);

     try {
       const formData = new FormData();

       for (const file of selectedFiles) {
         formData.append("files", file);
       }

       formData.append("checkLongConnections", checkLongConnections);
       formData.append("stdevX", stdevX);

       console.log("[AUTOCONNECT] Starting AutoConnect...");

       const response = await fetch("/nmo/connect_disjoint_subtrees", {
         method: "POST",
         body: formData,
       });

       const data = await response.json();

       console.log("[AUTOCONNECT] Initial response:", data);

       if (response.status === 429) {
         setSystemBusy(true);
         setIsSaving(false);

         window.alert(
           data.message ||
             "Another process is already running. Please wait until it finishes."
         );

         return;
       }

       if (!response.ok) {
         throw new Error(
           data.message ||
             `Unable to start AutoConnect (HTTP ${response.status})`
         );
       }

       const jobId = data.job_id;

       console.log("[AUTOCONNECT] Job ID:", jobId);

       //------------------------------------------------------------------
       // Poll until processing completes
       //------------------------------------------------------------------

       while (true) {
         await new Promise((resolve) => setTimeout(resolve, 5000));

         try {
           const statusResponse = await fetch(
             `/nmo/connect_status/${jobId}`,
             {
               cache: "no-store",
             }
           );

           if (!statusResponse.ok) {
             console.warn(
               "[AUTOCONNECT] Unable to retrieve status:",
               statusResponse.status
             );
             continue;
           }

           const status = await statusResponse.json();

           console.log("[AUTOCONNECT] Status:", status);

           if (status.status === "processing") {
             continue;
           }

           if (status.status === "completed") {
             console.log("[AUTOCONNECT] Processing completed.");

             const shouldDownload = window.confirm(
               "AutoConnect process completed successfully.\n\n" +
                 "Do you want to download the connected SWC files?"
             );

             if (shouldDownload) {
               await handleDownloadConnectClick();
             }

             break;
           }

           if (status.status === "failed") {
             throw new Error(
               status.message || "AutoConnect processing failed."
             );
           }
         } catch (pollError) {
           console.warn(
             "[AUTOCONNECT] Status polling failed. Retrying...",
             pollError
           );

           // Network interruption
           // Keep polling instead of failing the job.
           continue;
         }
       }

       setSystemBusy(false);
       setIsSaving(false);
     } catch (error) {
       console.error("[AUTOCONNECT] Error:", error);

       setIsSaving(false);
       setSystemBusy(false);

       window.alert(
         "AutoConnect failed.\n\n" +
           error.message +
           "\n\nIf processing is still running, the download will become available when the job completes."
       );
     }
   };



  const handleAutoTagClick = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      window.alert('Please select SWC files first.');
      return;
    }

    try {
      const response = await fetch('/nmo/CorrectTag', {
        method: 'POST',
        body: selectedFiles,
      });

      let responseText = '';

      try {
        responseText = await response.text();
      } catch (readError) {
        console.error(
          '[AUTOTAG] Could not read response body:',
          readError
        );
      }

      console.log(
        '[AUTOTAG] status:',
        response.status,
        response.statusText
      );
      console.log(
        '[AUTOTAG] response body:',
        responseText
      );

      if (response.status === 429) {
        setSystemBusy(true);
        setIsSaving(false);
        window.alert('System currently in use. Please try again later.');
        return;
      }

      if (response.ok) {
        const shouldDownload = window.confirm(
          'Auto Tag process completed successfully.\n' +
          'Do you want to download the files?'
        );

        if (shouldDownload) {
          await handleDownloadCorrectedTagClick();
        }

        setSystemBusy(false);
      } else {
        window.alert(
          `Failed to complete Tag correction process.\n\n` +
          `HTTP Status: ${response.status} ${response.statusText}\n\n` +
          `Server said:\n${responseText || 'No response body returned.'}`
        );
      }
    } catch (error) {
      console.error('[AUTOTAG] Error:', error);

      window.alert(
        `Error occurred during Tag Correction process.\n\n` +
        `${error.name}: ${error.message}`
      );
    }
  };

  const handleDownloadClick = async () => {
    try {
      const timestamp = new Date()
        .toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hour12: false,
        })
        .replace(/[/:,\s]/g, '_');

      const downloadUrl =
        `/nmo/download?timestamp=${timestamp}`;

      const response = await fetch(downloadUrl);

      console.log(
        '[DOWNLOAD STD] status:',
        response.status,
        response.statusText
      );

      if (!response.ok) {
        throw new Error(
          `HTTP error! Status: ${response.status}`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `swc_standardized_${timestamp}.zip`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);

      window.alert('Download completed successfully.');
    } catch (error) {
      console.error('[DOWNLOAD STD] Error:', error);

      window.alert(
        `Unable to download standardized files.\n\n` +
        `${error.name}: ${error.message}`
      );
    }
  };

  const handleDownloadConnectClick = async () => {
    try {
      const timestamp = new Date()
        .toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hour12: false,
        })
        .replace(/[/:,\s]/g, '_');

      const downloadUrl =
        `/nmo/download_connected?timestamp=${timestamp}`;

      console.log(
        '[DOWNLOAD CONNECT] URL:',
        downloadUrl
      );

      const response = await fetch(downloadUrl);

      console.log(
        '[DOWNLOAD CONNECT] status:',
        response.status,
        response.statusText
      );

      if (!response.ok) {
        throw new Error(
          `HTTP error! Status: ${response.status}`
        );
      }

      const blob = await response.blob();

      console.log(
        '[DOWNLOAD CONNECT] Blob size:',
        blob.size
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `swc_connected_${timestamp}.zip`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);

      window.alert('Download completed successfully.');
    } catch (error) {
      console.error('[DOWNLOAD CONNECT] Error:', error);

      window.alert(
        `Unable to download connected files.\n\n` +
        `${error.name}: ${error.message}`
      );
    }
  };

  const handleDownloadCorrectedTagClick = async () => {
    try {
      const timestamp = new Date()
        .toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hour12: false,
        })
        .replace(/[/:,\s]/g, '_');

      const downloadUrl =
        `/nmo/download_corrected_tags?timestamp=${timestamp}`;

      console.log(
        '[DOWNLOAD TAG] URL:',
        downloadUrl
      );

      const response = await fetch(downloadUrl);

      console.log(
        '[DOWNLOAD TAG] status:',
        response.status,
        response.statusText
      );

      if (!response.ok) {
        throw new Error(
          `HTTP error! Status: ${response.status}`
        );
      }

      const blob = await response.blob();

      console.log(
        '[DOWNLOAD TAG] Blob size:',
        blob.size
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `swc_auto_tag_${timestamp}.zip`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);

      window.alert('Download completed successfully.');
    } catch (error) {
      console.error('[DOWNLOAD TAG] Error:', error);

      window.alert(
        `Unable to download corrected tag files.\n\n` +
        `${error.name}: ${error.message}`
      );
    }
  };

  const sharedGridColumns = '360px 260px';

  const buttonStyle = {
    fontSize: '16px',
    padding: '6px 20px',
    backgroundColor: '#d3d3d3',
    color: 'green',
    border: '1px solid black',
    cursor: 'pointer',
    width: '140px',
    marginLeft: '5px',
    marginTop: '10px',
    marginBottom: '10px',
    marginRight: '20px',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const formStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginTop: '15px',
    marginLeft: '5px',
    marginBottom: '50px',
  };

  const row2GridStyle = {
    display: 'grid',
    gridTemplateColumns: sharedGridColumns,
    columnGap: '80px',
    alignItems: 'start',
    marginTop: '10px',
    marginBottom: '25px',
    marginLeft: '5px',
    position: 'relative',
  };

  const row3GridStyle = {
    display: 'grid',
    gridTemplateColumns: '360px 260px 260px',
    columnGap: '80px',
    marginBottom: '25px',
    marginLeft: '5px',
  };

  const checkboxLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const subRowStyle = {
    marginTop: '2px',
    marginLeft: '18px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'nowrap',
  };

  const selectStyle = {
    whiteSpace: 'nowrap',
    fontSize: '16px',
    fontFamily: 'inherit',
  };

  return (
    <div align="left">
      <img
        src={logo}
        width="700"
        height="100"
        alt="swc qc logo"
        style={{
          marginBottom: '10px',
          marginLeft: '5px',
          marginTop: '5px',
        }}
      />

      {/* ---------- ROW 1: Upload ---------- */}
      <form onSubmit={handleSubmit} style={formStyle}>
        <input
          type="file"
          multiple
          webkitdirectory="true"
          accept=".swc"
          onChange={handleFileChange}
          style={{
            color: 'green',
            width: '240px',
            fontSize: '16px',
          }}
        />

        <button
          type="submit"
          style={{
            ...buttonStyle,
            marginLeft: '80px',
          }}
        >
          {isSaving ? 'Saving...' : 'Upload To Server'}
        </button>
      </form>

      {/* ---------- ROW 2: Options ---------- */}
      <div style={row2GridStyle}>
        <div>
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={checkCorrectBranchTag}
              onChange={(e) =>
                setCheckCorrectBranchTag(e.target.checked)
              }
            />
            Correct Branch Tag
          </label>

          <div style={subRowStyle}>
            <label style={{ marginRight: '4px' }}>
              New BranchType
            </label>

            <span
              ref={branchTextRef}
              style={{
                position: 'absolute',
                visibility: 'hidden',
                whiteSpace: 'nowrap',
                fontSize: '16px',
                fontFamily: 'inherit',
                fontWeight: 'normal',
              }}
            >
              {branchLabels[branchtype]}
            </span>

            <select
              ref={branchSelectRef}
              value={branchtype}
              onChange={(e) =>
                setbranchtype(Number(e.target.value))
              }
              style={selectStyle}
            >
              <option value={2}>Axon (2)</option>
              <option value={3}>Basal dendrites (3)</option>
              <option value={4}>Apical dendrites (4)</option>
              <option value={5}>Other dendrites (5)</option>
              <option value={6}>Unspecified neurites (6)</option>
              <option value={7}>Glial processes (7)</option>
            </select>
          </div>
        </div>

        <div style={{ marginLeft: '-110px' }}>
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={checkLongConnections}
              onChange={(e) =>
                setCheckLongConnections(e.target.checked)
              }
            />
            Fix long connections
          </label>

          <div style={subRowStyle}>
            <label style={{ marginRight: '6px' }}>
              Threshold:
            </label>

            <span
              ref={stdevTextRef}
              style={{
                position: 'absolute',
                visibility: 'hidden',
                whiteSpace: 'nowrap',
                fontSize: '16px',
                fontFamily: 'inherit',
                fontWeight: 'normal',
              }}
            >
              {String(stdevX)}
            </span>

            <select
              ref={stdevSelectRef}
              value={stdevX}
              onChange={(e) =>
                setStdevX(Number(e.target.value))
              }
              style={selectStyle}
            >
              {Array.from(
                { length: 10 },
                (_, i) => i + 1
              ).map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>

            <label style={{ marginLeft: '1px' }}>
              SD
            </label>

          </div>
        </div>
      </div>

      {/* ---------- ROW 3: Actions ---------- */}
      <div style={row3GridStyle}>
        <div>
          <button
            onClick={handleStandardizeClick}
            style={buttonStyle}
          >
            Standardize
          </button>
        </div>

        <div style={{ marginLeft: '-110px' }}>
          <button
            onClick={handleAutoConnectClick}
            style={buttonStyle}
          >
            Auto Connect
          </button>
        </div>

        <div style={{ marginLeft: '-200px' }}>
          <button
            onClick={handleAutoTagClick}
            style={buttonStyle}
          >
            Auto Tag Apical
          </button>
        </div>
      </div>

      {/* ---------- Logs ---------- */}
      <LogViewer />
    </div>
  );
}

export default App;
